/**
 * Custom settings page that wraps the standard Backstage SettingsLayout,
 * adding an "API Tokens" tab alongside the built-in tabs.
 *
 * We copy extension metadata (core.extensionName, core.mountPoint) from the stock
 * UserSettingsPage so that convertLegacyPageExtension in App.tsx can bind the
 * user-settings routeRef to /settings. Without this, UserSettingsSignInAvatar in
 * Root.tsx throws "No path for routeRef{type=absolute,id=user-settings}" because
 * the routeRef is never registered.
 */
import React from 'react';
import {
  SettingsLayout,
  UserSettingsGeneral,
  UserSettingsAuthProviders,
  UserSettingsFeatureFlags,
  UserSettingsPage,
} from '@backstage/plugin-user-settings';
import {
  getComponentData,
  attachComponentData,
  useApi,
  configApiRef,
} from '@backstage/core-plugin-api';
import { ApiTokensSettingsTab } from './ApiTokensSettingsTab';

export function SettingsPage() {
  const configApi = useApi(configApiRef);
  const apiTokensEnabled =
    configApi.getOptionalBoolean('apiTokens.enabled') ?? false;

  return (
    <SettingsLayout>
      <SettingsLayout.Route path="general" title="General">
        <UserSettingsGeneral />
      </SettingsLayout.Route>
      <SettingsLayout.Route path="auth-providers" title="Auth Providers">
        <UserSettingsAuthProviders />
      </SettingsLayout.Route>
      <SettingsLayout.Route path="feature-flags" title="Feature Flags">
        <UserSettingsFeatureFlags />
      </SettingsLayout.Route>
      {apiTokensEnabled && (
        <SettingsLayout.Route path="api-tokens" title="API Tokens">
          <ApiTokensSettingsTab />
        </SettingsLayout.Route>
      )}
    </SettingsLayout>
  );
}

// Attach the same extension metadata that UserSettingsPage carries so that
// convertLegacyPageExtension(SettingsPage, ...) in App.tsx correctly binds the
// user-settings plugin's routeRef to /settings.
const _ref = React.createElement(UserSettingsPage, {});
attachComponentData(
  SettingsPage,
  'core.extensionName',
  getComponentData(_ref, 'core.extensionName'),
);
attachComponentData(
  SettingsPage,
  'core.mountPoint',
  getComponentData(_ref, 'core.mountPoint'),
);
